import { Dialog, DialogContent, Switch } from '@mui/material';
import { t } from 'i18next';
import { CSSProperties, useEffect, useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';
import { MainButton } from '../Buttons/MainButton';
import CircleLoadingIndicator from '../LoadingIndicators/CircleLoadingIndicator';
import {
  simpleModalActionsContainerStyles,
  simpleModalPaperDynamicSizeRootStyles,
  simpleModalPaperFixedWidthStyles,
} from '../Modals/styles/simpleModalStyles';
import {
  UPDATE_NOTIFICATION_PREFERENCES,
  UpdateNotificationPreferencesInput,
} from '../../graphql/apis/notifications/mutations/pushNotificationMutations';
import { useGetMyNotificationPreferences } from '../../graphql/apis/notifications/queries/hooks/useGetMyNotificationPreferences';
import { colors } from '../../theme/colors';
import spacing from '../../theme/spacing';
import { fontSize } from '../../theme/typography';

interface NotificationPreferencesModalProps {
  isVisible: boolean;
  onClose: () => void;
}

interface PreferenceItem {
  key: keyof UpdateNotificationPreferencesInput;
  title: string;
}

type Preferences = {
  pushNotifications: boolean;
  exchangeUpdates: boolean;
  newFollowers: boolean;
  assetSaved: boolean;
  chatMessages: boolean;
};

const areAllIndividualPreferencesEnabled = (preferences: Preferences) => {
  return (
    Boolean(preferences.exchangeUpdates) &&
    Boolean(preferences.newFollowers) &&
    Boolean(preferences.assetSaved) &&
    Boolean(preferences.chatMessages)
  );
};

const extractPreferencesFromResponse = (data: any): Preferences | null => {
  const preferences = data?.getMyNotificationPreferences;
  if (!preferences) return null;

  return {
    pushNotifications: Boolean(preferences.pushNotifications),
    exchangeUpdates: Boolean(preferences.exchangeUpdates),
    newFollowers: Boolean(preferences.newFollowers),
    assetSaved: Boolean(preferences.assetSaved),
    chatMessages: Boolean(preferences.chatMessages),
  };
};

const buildUpdatePreferencesInput = (
  preferences: Preferences,
): UpdateNotificationPreferencesInput => {
  return {
    pushNotifications: preferences.pushNotifications,
    exchangeUpdates: preferences.exchangeUpdates,
    newFollowers: preferences.newFollowers,
    assetSaved: preferences.assetSaved,
    chatMessages: preferences.chatMessages,
  };
};

const NotificationPreferencesModal = ({
  isVisible,
  onClose,
}: NotificationPreferencesModalProps) => {
  const { data, loading } = useGetMyNotificationPreferences({
    skip: !isVisible,
    fetchPolicy: 'no-cache',
    nextFetchPolicy: 'no-cache',
    returnPartialData: false,
    notifyOnNetworkStatusChange: true,
  });
  const [updateNotificationPreferences] = useMutation(
    UPDATE_NOTIFICATION_PREFERENCES,
  );
  const [preferences, setPreferences] = useState<Preferences | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!isVisible) {
      setPreferences(null);
    }
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible || loading) return;

    const nextPreferences = extractPreferencesFromResponse(data);
    if (nextPreferences) {
      const allEnabled = areAllIndividualPreferencesEnabled(nextPreferences);
      setPreferences({ ...nextPreferences, pushNotifications: allEnabled });
    }
  }, [isVisible, data, loading]);

  const preferenceItems: PreferenceItem[] = useMemo(
    () => [
      {
        key: 'exchangeUpdates',
        title: t('notifications.preferences.exchangeUpdates.title'),
      },
      {
        key: 'chatMessages',
        title: t('notifications.preferences.chatMessages.title'),
      },
      {
        key: 'newFollowers',
        title: t('notifications.preferences.newFollowers.title'),
      },
      {
        key: 'assetSaved',
        title: t('notifications.preferences.assetSaved.title'),
      },
    ],
    [],
  );

  const handleToggle = (key: keyof UpdateNotificationPreferencesInput) => {
    setPreferences((previousPreferences) => {
      if (!previousPreferences) return previousPreferences;

      const updatedPreferences = {
        ...previousPreferences,
        [key]: !previousPreferences[key],
      } as Preferences;
      const allEnabled = areAllIndividualPreferencesEnabled(updatedPreferences);

      return { ...updatedPreferences, pushNotifications: allEnabled };
    });
  };

  const handleToggleMaster = () => {
    if (!preferences) return;

    const nextValue = !preferences.pushNotifications;
    setPreferences({
      ...preferences,
      pushNotifications: nextValue,
      exchangeUpdates: nextValue,
      newFollowers: nextValue,
      assetSaved: nextValue,
      chatMessages: nextValue,
    });
  };

  const handleSave = async () => {
    if (!preferences || isSaving) return;

    setIsSaving(true);

    try {
      const response = await updateNotificationPreferences({
        variables: {
          input: buildUpdatePreferencesInput(preferences),
        },
      });

      if (response.data?.updateNotificationPreferences) {
        onClose();
      }
    } catch (error) {
      console.error('Error saving notification preferences:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const isBusy = loading || !preferences;

  return (
    <Dialog
      open={isVisible}
      onClose={onClose}
      sx={{
        '& .MuiPaper-root': {
          ...simpleModalPaperDynamicSizeRootStyles,
          ...simpleModalPaperFixedWidthStyles,
          maxHeight: '90%',
        },
      }}
    >
      <DialogContent
        style={{
          paddingLeft: spacing.mediumLarge,
          paddingRight: spacing.mediumLarge,
          paddingBottom: spacing.mediumLarge,
        }}
      >
        <div style={styles.title}>{t('notifications.preferences.title')}</div>

        {isBusy ? (
          <div style={styles.loadingContainer}>
            <CircleLoadingIndicator />
          </div>
        ) : (
          <div style={styles.scrollContainer}>
            <div style={styles.preferenceItem}>
              <div style={styles.preferenceText}>
                <div style={{ ...styles.preferenceTitle, fontWeight: 'bold' }}>
                  {t('notifications.preferences.masterTurnAllOn')}
                </div>
              </div>
              <Switch
                checked={preferences.pushNotifications}
                onChange={handleToggleMaster}
              />
            </div>

            {preferenceItems.map((item) => (
              <div key={item.key} style={styles.preferenceItem}>
                <div style={styles.preferenceText}>
                  <div style={styles.preferenceTitle}>{item.title}</div>
                </div>
                <Switch
                  checked={Boolean(preferences[item.key])}
                  onChange={() => handleToggle(item.key)}
                />
              </div>
            ))}
          </div>
        )}
      </DialogContent>

      {!isBusy && (
        <div style={simpleModalActionsContainerStyles}>
          <MainButton
            onClick={onClose}
            style={{
              color: colors.textPrimary,
              fontWeight: 'normal',
            }}
            disabled={isSaving}
            typeStyle="link"
            text={t('common.close')}
          />
          <MainButton
            onClick={handleSave}
            typeStyle="link"
            text={t('common.save')}
            style={{ color: colors.primary }}
            disabled={isSaving || loading}
          />
        </div>
      )}
    </Dialog>
  );
};

const styles = {
  title: {
    fontSize: fontSize.lg,
    color: colors.textPrimary,
    fontWeight: 'bold',
    textAlign: 'left',
    marginBottom: spacing.large,
  } as CSSProperties,
  loadingContainer: {
    minHeight: 150,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  } as CSSProperties,
  scrollContainer: {
    minHeight: 150,
    maxHeight: '60vh',
    overflowY: 'auto',
    marginBottom: spacing.medium,
  } as CSSProperties,
  preferenceItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: 16,
    paddingBottom: 16,
    borderBottom: `1px solid ${colors.backgroundSuperLight}`,
  } as CSSProperties,
  preferenceText: {
    flex: 1,
    marginRight: 16,
  } as CSSProperties,
  preferenceTitle: {
    color: colors.textPrimary,
    marginBottom: 4,
    fontSize: 16,
    fontWeight: '500',
  } as CSSProperties,
};

export default NotificationPreferencesModal;
